import { creatorbase, creatorbaseQueries } from '@round/api';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { useCampaigns, useAllPosts } from '@round/creatorbase-helpers/api-hooks';

const CAMPAIGNS_PAGE_SIZE = 100;

type Config = {
    enabled: boolean;
};

export function useProjectPostsData(projectId: number | null | undefined, config: Config = { enabled: true }) {
    const queryClient = useQueryClient();

    const campaignsQuery = useCampaigns(
        { project_id: projectId!, page_size: CAMPAIGNS_PAGE_SIZE },
        { ...config, enabled: config.enabled && !!projectId, placeholderData: keepPreviousData }
    );

    const campaignIds = campaignsQuery.data?.results.map((c) => c.id) ?? [];
    const postsListParams = { campaign_id: campaignIds.toString() };
    const postsQuery = useAllPosts(postsListParams, {
        enabled: !!campaignIds.length,
        placeholderData: keepPreviousData,
    });

    const postsListKey = creatorbaseQueries.posts.keys.list(postsListParams);

    const addPostToState = (post: creatorbase.Post) => {
        queryClient.setQueryData(postsListKey, (prev: creatorbase.Post[] | undefined) => [...(prev ?? []), post]);
    };
    const removePostFromState = (postId: string) => {
        queryClient.setQueryData(
            postsListKey,
            (prev: creatorbase.Post[] | undefined) => prev?.filter((p) => p.id !== postId) ?? []
        );
    };

    return {
        campaigns: campaignsQuery.data?.results,
        posts: postsQuery.data,
        addPostToState,
        removePostFromState,
        refetch: () => {
            campaignsQuery.refetch();
            postsQuery.refresh();
        },
        isLoading: campaignsQuery.isLoading || postsQuery.isLoading,
        isFetching: campaignsQuery.isFetching || postsQuery.isFetching,
    };
}
